import * as React from 'react';

import {getReadableDelay, getReadableDelayUnit} from 'nutshell-core/date-time/delays';
import type {Delay} from 'nutshell-core/types';

import {TextField} from 'shells/textfield/textfield';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';

import styles from './email-delay.module.css';

type Props = {
    delay: Delay | null | undefined;
    onTimespanChange?: (newTimespan?: string | null | undefined) => void;
    expectedSendTime?: number;
    readOnly?: boolean;
};

type State = {
    timespan: string;
};

export class EmailDelay extends React.PureComponent<Props, State> {
    input: HTMLInputElement | null | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            timespan: props.delay ? String(props.delay.timespan) : '0',
        };
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    render() {
        const {delay, expectedSendTime, onTimespanChange} = this.props;

        if (!delay || delay.timespan === 0) return null;

        // Expand/contract field based on num of characters
        const timespan = this.state.timespan;
        let inputWidth = 27;
        if (timespan.length === 2) inputWidth = 37;
        if (timespan.length === 3) inputWidth = 47;

        return (
            <div className={styles['email-delay']}>
                {onTimespanChange ? (
                    <div className={styles['configurable-delay']}>
                        <div style={{width: inputWidth}}>
                            <TextField
                                type='number'
                                min='0'
                                max='365'
                                value={timespan}
                                onChange={this.handleTimespanChange}
                                // @ts-expect-error
                                onBlur={this.handleBlur}
                                textFieldRef={(node) => {
                                    this.input = node;
                                }}
                                selectOnFocus={true}
                                disabled={this.props.readOnly}
                            />
                        </div>
                        <span className={styles.unit}>
                            {getReadableDelayUnit(delay)} later, if no reply
                        </span>
                    </div>
                ) : (
                    <div>{`${getReadableDelay(delay)} later, if no reply`}</div>
                )}
                {expectedSendTime ? (
                    <FormattedTimestamp
                        format={FormattedTimestamp.formats.monthDay}
                        datetime={expectedSendTime}
                    />
                ) : null}
            </div>
        );
    }

    handleTimespanChange = (timespan: string) => {
        this.setState({timespan});
    };

    handleBlur = () => {
        const {onTimespanChange} = this.props;
        if (!onTimespanChange) return;

        const timespan = this.state.timespan;
        if (!timespan || timespan === '0') {
            // Don't allow submitting zero or empty values
            this.setState({timespan: '1'});
            onTimespanChange('1');
        } else {
            onTimespanChange(timespan);
        }
    };

    // @ts-expect-error
    handleKeyDown: KeyboardEventListener = (e) => {
        if (e.keyCode === Nut.keys.RETURN && !e.defaultPrevented && this.input) {
            this.input.blur();
        }
    };
}
